<div class="bg-white h-full flex flex-col">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-100">
        <div class="flex items-center justify-between">
            <div>
                <h2 class="text-lg font-semibold text-gray-900">{{ 'AUTO_RECHARGE_TITLE' | translate }}</h2>
                <p class="text-sm text-gray-500 mt-0.5">{{ 'AUTO_RECHARGE_SUBTITLE' | translate }}</p>
            </div>
            <button (click)="close()"
                    class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <i class="fa-solid fa-xmark text-gray-500"></i>
            </button>
        </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-6 py-5">
        <!-- Loading State -->
        <div *ngIf="loading" class="flex flex-col items-center justify-center py-12">
            <ion-spinner name="crescent" class="text-primary"></ion-spinner>
        </div>

        <div *ngIf="!loading">
            <!-- Info banner -->
            <div class="mb-5 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-100">
                <div class="flex items-center gap-3">
                    <div class="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                        <i class="fa-solid fa-bolt text-amber-600 text-sm"></i>
                    </div>
                    <p class="flex-1 text-sm text-amber-800">{{ 'AUTO_RECHARGE_INFO' | translate }}</p>
                </div>
            </div>

            <!-- Enable toggle -->
            <div class="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                    <div class="text-sm font-semibold text-gray-900">{{ 'AUTO_RECHARGE_ENABLE' | translate }}</div>
                    <div class="text-xs text-gray-500 mt-0.5">{{ 'AUTO_RECHARGE_ENABLE_HINT' | translate }}</div>
                </div>
                <ion-toggle [(ngModel)]="enabled" color="warning"></ion-toggle>
            </div>

            <!-- Config fields (disabled visually when off) -->
            <div class="mt-4 space-y-5" [class.opacity-50]="!enabled" [class.pointer-events-none]="!enabled">
                <!-- Threshold -->
                <div>
                    <label class="block text-sm font-medium text-gray-900">{{ 'AUTO_RECHARGE_THRESHOLD_LABEL' | translate }}</label>
                    <p class="text-xs text-gray-500 mt-0.5 mb-2">{{ 'AUTO_RECHARGE_THRESHOLD_HINT' | translate }}</p>
                    <div class="relative">
                        <input type="number"
                               [(ngModel)]="thresholdCredits"
                               [min]="thresholdMin" [max]="thresholdMax" step="10"
                               class="ar-input w-full h-11 px-3 pr-20 text-sm"
                               [class.ar-input--error]="enabled && thresholdError" />
                        <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{{ 'CREDITS' | translate }}</span>
                    </div>
                    <div class="mt-1 flex items-center justify-between gap-2">
                        <span *ngIf="enabled && thresholdError" class="text-xs text-red-600 flex items-center gap-1">
                            <i class="fa-solid fa-circle-exclamation"></i>
                            {{ thresholdError.key | translate:thresholdError.params }}
                        </span>
                        <span class="text-xs text-gray-400 ml-auto">{{ 'AUTO_RECHARGE_RANGE' | translate:{ min: thresholdMin, max: thresholdMax } }}</span>
                    </div>
                </div>

                <!-- Recharge amount -->
                <div>
                    <label class="block text-sm font-medium text-gray-900">{{ 'AUTO_RECHARGE_AMOUNT_LABEL' | translate }}</label>
                    <p class="text-xs text-gray-500 mt-0.5 mb-2">{{ 'AUTO_RECHARGE_AMOUNT_HINT' | translate }}</p>
                    <div class="relative">
                        <input type="number"
                               [(ngModel)]="rechargeCredits"
                               [min]="amountMin" [max]="amountMax" step="100"
                               class="ar-input w-full h-11 px-3 pr-20 text-sm"
                               [class.ar-input--error]="enabled && rechargeError" />
                        <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{{ 'CREDITS' | translate }}</span>
                    </div>
                    <div class="mt-1 flex items-center justify-between gap-2">
                        <span *ngIf="enabled && rechargeError" class="text-xs text-red-600 flex items-center gap-1">
                            <i class="fa-solid fa-circle-exclamation"></i>
                            {{ rechargeError.key | translate:rechargeError.params }}
                        </span>
                        <span class="text-xs text-gray-400 ml-auto">{{ 'AUTO_RECHARGE_RANGE' | translate:{ min: amountMin, max: amountMax } }}</span>
                    </div>
                    <!-- Approx charge amount -->
                    <div *ngIf="!rechargeError" class="mt-1.5 text-xs text-gray-500 flex items-center gap-1.5">
                        <i class="fa-solid fa-credit-card text-amber-500"></i>
                        <span>{{ 'AUTO_RECHARGE_APPROX' | translate }} <strong>≈ {{ rechargeApprox }} €</strong> {{ 'AUTO_RECHARGE_APPROX_SUFFIX' | translate }}</span>
                    </div>
                </div>
            </div>

            <!-- Error -->
            <div *ngIf="error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <i class="fa-solid fa-circle-exclamation"></i>
                <span class="text-sm">{{ error | translate }}</span>
            </div>
        </div>
    </div>

    <!-- Footer -->
    <div class="px-6 py-3 border-t border-gray-100 bg-gray-50">
        <div class="flex justify-end gap-2">
            <button (click)="close()"
                    type="button"
                    class="h-9 px-5 rounded-lg bg-white text-gray-600 text-sm font-medium transition-colors border border-gray-300 hover:bg-gray-50">
                {{ 'CANCEL' | translate }}
            </button>
            <button (click)="save()"
                    type="button"
                    [disabled]="saving || (enabled && !isValid)"
                    class="h-9 px-6 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                <ion-spinner *ngIf="saving" name="crescent" class="w-4 h-4"></ion-spinner>
                <span>{{ saving ? ('PROCESSING' | translate) : ('SAVE' | translate) }}</span>
            </button>
        </div>
    </div>
</div>
